/**
 * Web Notification Service Implementation
 * Browser notification implementation for web platform
 */

import type { 
  INotificationService, 
  NotificationPayload, 
  NotificationPermissionStatus 
} from '@hemberedskapsappen/shared';

class WebNotificationService implements INotificationService {
  private isInitialized = false;

  private isReactNativeWebView(): boolean {
    return !!window.ReactNativeWebView;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!('Notification' in window)) {
      console.warn('[WebNotificationService] This browser does not support notifications');
      return;
    }

    this.isInitialized = true;
  }

  async getPermissionStatus(): Promise<NotificationPermissionStatus> {
    // In WebView, React Native handles permissions, so always return granted
    if (this.isReactNativeWebView()) {
      return { granted: true, canAskAgain: false };
    }

    if (!('Notification' in window)) {
      return { granted: false, canAskAgain: false };
    }

    return {
      granted: Notification.permission === 'granted',
      canAskAgain: Notification.permission === 'default',
    };
  }

  async requestPermission(): Promise<boolean> {
    // In WebView, React Native handles permissions
    if (this.isReactNativeWebView()) {
      return true; // React Native will handle permission requests
    }

    if (!('Notification' in window)) {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  async sendLocalNotification(payload: NotificationPayload): Promise<void> {
    // Check if running in React Native WebView FIRST
    // WebView has its own notification system and doesn't use browser Notification API
    if (this.isReactNativeWebView()) {
      // Send notification request to React Native (it handles its own permissions)
      try {
        window.ReactNativeWebView?.postMessage(JSON.stringify({
          type: 'SEND_NOTIFICATION',
          payload: {
            title: payload.title,
            body: payload.body,
            data: payload.data,
            sound: payload.sound,
          }
        }));
        console.log('[WebNotificationService] Sent notification to React Native:', payload.title);
      } catch (error) {
        console.error('[WebNotificationService] Failed to send notification to React Native:', error);
      }
      return;
    }

    // For normal web browsers, check permissions
    const status = await this.getPermissionStatus();
    if (!status.granted) {
      console.warn('[WebNotificationService] Cannot send notification: permission not granted');
      return;
    }

    // Browser notification for web
    if (!('Notification' in window)) {
      console.warn('[WebNotificationService] Browser does not support notifications');
      return;
    }

    const notification = new Notification(payload.title, {
      body: payload.body,
      icon: '/icon.png',
      badge: '/badge.png',
      tag: payload.data?.type as string || 'default',
      data: payload.data,
    });

    // Handle notification click
    notification.onclick = () => {
      // Store the notification data for the app to handle
      if (payload.data) {
        sessionStorage.setItem('pendingNotificationAction', JSON.stringify(payload.data));
      }
      
      // Focus or open the window
      window.focus();
      notification.close();
    };
  }

  async configureNotifications(): Promise<void> {
    // No configuration needed for web
  }

  getExpoPushToken(): string | null {
    // Web doesn't use Expo push tokens
    return null;
  }
}

// Export singleton instance
export const notificationService = new WebNotificationService();
